import { useMemo } from 'react';
import { PresentationTreeNetwork, TaxonomyChild } from 'app/api/tag-queries/types';

export interface SearchEntry {
  nodeId: string;
  searchText: string;
  networkIdx: number;
  ancestorIds: string[];
}

function collectNodes(children: TaxonomyChild[], networkIdx: number, networkDefinition: string, ancestorPath: string[], entries: SearchEntry[]): void {
  for (const child of children) {
    const parts: string[] = [child.id, networkDefinition];

    if (child.labels) {
      for (const lang of Object.keys(child.labels)) {
        const langLabels = child.labels[lang];
        for (const type of Object.keys(langLabels)) {
          parts.push(langLabels[type]);
        }
      }
    }

    entries.push({
      nodeId: child.id,
      searchText: parts.join(' ').toLowerCase(),
      networkIdx,
      ancestorIds: ancestorPath,
    });

    if (child.children?.length > 0) {
      collectNodes(child.children, networkIdx, networkDefinition, [...ancestorPath, child.id], entries);
    }
  }
}

export function buildSearchIndex(networks: PresentationTreeNetwork[]): SearchEntry[] {
  const entries: SearchEntry[] = [];
  for (let i = 0; i < networks.length; i++) {
    const network = networks[i];
    const definition = network.linkrole.definition;
    const linkroleTexts: string[] = [definition];
    if (network.linkrole.labels) {
      for (const lang of Object.keys(network.linkrole.labels)) {
        const text = network.linkrole.labels[lang];
        if (text) linkroleTexts.push(text);
      }
    }
    const networkSearchText = linkroleTexts.join(' ');
    for (const root of network.roots) {
      // Index the root node itself
      const rootParts: string[] = [root.id, networkSearchText];
      if (root.labels) {
        for (const lang of Object.keys(root.labels)) {
          const langLabels = root.labels[lang];
          for (const type of Object.keys(langLabels)) {
            rootParts.push(langLabels[type]);
          }
        }
      }
      entries.push({
        nodeId: root.id,
        searchText: rootParts.join(' ').toLowerCase(),
        networkIdx: i,
        ancestorIds: [],
      });

      collectNodes(root.children, i, networkSearchText, [], entries);
    }
  }
  return entries;
}

export interface TaxonomySearchResult {
  filteredNetworkIndices: Set<number> | null;
  searchExpandIdsByNetwork: Map<number, Set<string>> | null;
  searchVisibleIdsByNetwork: Map<number, Set<string>> | null;
  isSearching: boolean;
}

export function useTaxonomySearch(networks: PresentationTreeNetwork[] | undefined, debouncedSearch: string): TaxonomySearchResult {
  const index = useMemo(() => buildSearchIndex(networks ?? []), [networks]);

  return useMemo(() => {
    if (!debouncedSearch.trim()) {
      return { filteredNetworkIndices: null, searchExpandIdsByNetwork: null, searchVisibleIdsByNetwork: null, isSearching: false };
    }

    const terms = debouncedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    const matches = index.filter((entry) => terms.every((t) => entry.searchText.includes(t)));

    const filteredNetworkIndices = new Set<number>();
    const searchExpandIdsByNetwork = new Map<number, Set<string>>();
    const searchVisibleIdsByNetwork = new Map<number, Set<string>>();

    const getOrCreate = (m: Map<number, Set<string>>, idx: number) => {
      let s = m.get(idx);
      if (!s) {
        s = new Set<string>();
        m.set(idx, s);
      }
      return s;
    };

    for (const match of matches) {
      filteredNetworkIndices.add(match.networkIdx);
      getOrCreate(searchVisibleIdsByNetwork, match.networkIdx).add(match.nodeId);
      for (const ancestorId of match.ancestorIds) {
        getOrCreate(searchExpandIdsByNetwork, match.networkIdx).add(ancestorId);
        getOrCreate(searchVisibleIdsByNetwork, match.networkIdx).add(ancestorId);
      }
    }

    return { filteredNetworkIndices, searchExpandIdsByNetwork, searchVisibleIdsByNetwork, isSearching: true };
  }, [index, debouncedSearch]);
}
